import { bridgeErrorKind } from '@heidi/plugin-core';
import { rpaBridge } from '@heidi/plugin-rpa';
import { logger, sanitizeError } from '@heidi/runtime/logger';
import { isTauri } from '@heidi/runtime/platform';

import type { ClientCapabilities } from '../../contracts';
type PlatformSystem = ClientCapabilities['platform_system'];

const KNOWN_PLATFORMS: readonly PlatformSystem[] = ['darwin', 'win32', 'linux'];

export interface ClientCapabilityOptions {
  /**
   * Advertise the recording remote-tool action in the capability snapshot.
   * The Project assembly always passes `true` — native build capability is
   * the FE gate, and the backend then gates the recording child per-user via
   * its own `release-scribe-enable-rpa-recording` check. Defaults off so
   * non-Project callers (e.g. prechart) never advertise recording.
   */
  advertiseRecordingRemoteTool?: boolean;
}

export const normalisePlatform = (value: string): PlatformSystem => {
  const lower = value.toLowerCase();
  for (const candidate of KNOWN_PLATFORMS) {
    if (lower.includes(candidate)) return candidate;
  }
  if (lower.includes('mac')) return 'darwin';
  if (lower.includes('windows')) return 'win32';
  return 'darwin';
};

/** Narrow `platform_system` for the project/workflow request body. */
export const toBackendClientCapabilities = (
  caps: Omit<ClientCapabilities, 'platform_system'> & { platform_system: string }
): ClientCapabilities => ({
  ...caps,
  platform_system: caps.platform_system as PlatformSystem,
});

/**
 * Declare that this bundle renders the ``pick_file`` clarification card.
 * The card is WebView code shipped with this bundle, so the FE grafts
 * the bit onto an existing ``file_tools`` catalog
 * in the assembly (never at call sites). Without a catalog there is
 * nothing to pick from, so the geometry-only fallback stays untouched.
 */
const declarePickFileClarification = (caps: ClientCapabilities): ClientCapabilities =>
  caps.file_tools
    ? { ...caps, file_tools: { ...caps.file_tools, pick_file_clarification: true } }
    : caps;

/**
 * Remove only the recording remote-tool action, keeping any other actions the
 * Rust catalog may advertise. Deleting the whole ``remote_tools`` container
 * would silently kill future remote actions that are unrelated to the
 * recording release gate.
 */
export const RECORDING_REMOTE_TOOL_ACTION = 'capture_human_computer_use_recording';

const filterRemoteToolAction = (caps: ClientCapabilities): ClientCapabilities => {
  const actions = caps.remote_tools?.actions;
  if (!actions?.length) {
    if (!caps.remote_tools) return caps;
    const withoutRemoteTools = { ...caps };
    delete withoutRemoteTools.remote_tools;
    return withoutRemoteTools;
  }
  const filtered = actions.filter((action) => action !== RECORDING_REMOTE_TOOL_ACTION);
  if (filtered.length === actions.length) return caps;
  if (filtered.length === 0) {
    const withoutRemoteTools = { ...caps };
    delete withoutRemoteTools.remote_tools;
    return withoutRemoteTools;
  }
  return { ...caps, remote_tools: { actions: filtered } };
};

export const assembleProjectClientCapabilities = (
  caps: Parameters<typeof toBackendClientCapabilities>[0],
  { advertiseRecordingRemoteTool = false }: ClientCapabilityOptions = {}
): ClientCapabilities => {
  const backendCaps = declarePickFileClarification(toBackendClientCapabilities(caps));
  return advertiseRecordingRemoteTool ? backendCaps : filterRemoteToolAction(backendCaps);
};

const detectBrowserPlatform = (): PlatformSystem => {
  if (typeof navigator === 'undefined') return 'darwin';
  return normalisePlatform(navigator.userAgent);
};

/** Geometry-only wire payload when the bridge cannot advertise catalogs. */
const getBrowserCapabilities = (): ClientCapabilities | null => {
  if (typeof window === 'undefined') return null;
  return {
    platform_system: detectBrowserPlatform(),
    screen_width: window.screen?.width ?? 1920,
    screen_height: window.screen?.height ?? 1080,
    scale_factor: window.devicePixelRatio || 1,
  };
};

/**
 * Best capability snapshot as the backend wire shape, or `null` when no
 * desktop client is attached. Never throws — detection must not block create.
 */
export const getClientCapabilities = async ({
  advertiseRecordingRemoteTool = false,
}: ClientCapabilityOptions = {}): Promise<ClientCapabilities | null> => {
  if (!isTauri()) return null;

  if (rpaBridge.isAvailable()) {
    try {
      return assembleProjectClientCapabilities(await rpaBridge.getCapabilities(), {
        advertiseRecordingRemoteTool,
      });
    } catch (error) {
      logger.warn(
        '[projects] rpaBridge.getCapabilities failed; falling back to window.screen',
        { errorKind: bridgeErrorKind(error), error: sanitizeError(error) }
      );
    }
  }
  return getBrowserCapabilities();
};

/** Project assembly — always advertises the recording remote-tool action. */
export const getProjectClientCapabilities = () =>
  getClientCapabilities({ advertiseRecordingRemoteTool: true });
